import { Link, useLocation } from "react-router-dom";
import { SeoTags } from "../../components/Head/SeoTags";
import { useEffect } from "react";
import { LogoOnly } from "../../assets/icons/logoOnly";
import { useTranslation } from "react-i18next";
import { stripLocalePrefix } from "../../utils/localePaths";

const faqData = [
  {
    questionKey: "pocketAlternative.faq.importData.question",
    answerKey: "pocketAlternative.faq.importData.answer"
  },
  {
    questionKey: "pocketAlternative.faq.free.question",
    answerKey: "pocketAlternative.faq.free.answer"
  },
  {
    questionKey: "pocketAlternative.faq.migrationTime.question", 
    answerKey: "pocketAlternative.faq.migrationTime.answer"
  },
  {
    questionKey: "pocketAlternative.faq.shutdown.question",
    answerKey: "pocketAlternative.faq.shutdown.answer"
  },
  {
    questionKey: "pocketAlternative.faq.mobile.question",
    answerKey: "pocketAlternative.faq.mobile.answer"
  },
  {
    questionKey: "pocketAlternative.faq.export.question",
    answerKey: "pocketAlternative.faq.export.answer"
  }
];

const PocketAlternative = () => {
  const location = useLocation();
  const { t } = useTranslation();

  // Slightly different titles and descriptions based on the route for better SEO.
  // Match on the locale-stripped base path so /es/pocket-shutdown etc. still
  // resolve to the right variant instead of falling through to the default.
  const getSeoByRoute = () => {
    const { basePath } = stripLocalePrefix(location.pathname);
    switch (basePath) {
      case "/pocket-shutdown":
        return {
          title: t("pocketAlternative.seo.titleByRoute.shutdown"),
          description: "Pocket is shutting down and you need an alternative. onBookmarks offers seamless migration, AI-powered organization, and client-side encryption. Import your Pocket bookmarks in seconds.",
        };
      case "/migrate-from-pocket":
        return {
          title: t("pocketAlternative.seo.titleByRoute.migrate"),
          description: "Complete guide to migrate from Pocket to onBookmarks. Export your Pocket data and import all bookmarks, tags, and reading status in under 2 minutes. Free migration tool included.",
        };
      case "/pocket-replacement":
        return {
          title: t("pocketAlternative.seo.titleByRoute.replacement"),
          description: "Looking for a Pocket replacement? onBookmarks is the best alternative with AI tagging, multi-collection organization, client-side encryption, and cross-device sync. Free forever plan available.",
        };
      default:
        return {
          title: t("pocketAlternative.seo.title"),
          description: t("pocketAlternative.seo.description"),
        };
    }
  };

  const seoData = getSeoByRoute();

  // use efect to scroll to the migration-guide found on the url #migration-guide
  useEffect(() => {
    if (location.hash === "#migration-guide") {
      const element = document.getElementById("migration-guide");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location.hash]);

  return (
    <>
      {/* All variant routes (/pocket-shutdown, /migrate-from-pocket,
          /pocket-replacement) canonical to /pocket-alternative: GSC reports
          them as "Duplicate, Google chose different canonical than user" —
          Google already consolidated them, so make it explicit and stop
          splitting signals. path also redirects the hreflang cluster there. */}
      <SeoTags
        title={seoData.title}
        description={seoData.description}
        keywords={t("pocketAlternative.seo.keywords")}
        path="/pocket-alternative"
      />

      {/* Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "onBookmarks",
            applicationCategory: "ProductivityApplication",
            operatingSystem: "Web, iOS, Android",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
            alternativeHeadline: "Best Pocket Alternative After Shutdown",
            description:
              "onBookmarks is the perfect replacement for Pocket. Import all your saved articles and discover AI-powered organization, encryption, and features Pocket never offered.",
          }),
        }}
      />

      {/* FAQ Schema for better visibility */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqData.map(faq => ({
              "@type": "Question",
              name: t(faq.questionKey),
              acceptedAnswer: {
                "@type": "Answer",
                text: t(faq.answerKey),
              },
            })),
          }),
        }}
      />

      {/* Hero Section - Emotional Connection */}
      <section className="hero bg-light">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-8 mx-auto text-center">
              <div>
                <div className="d-inline-block rounded-4 bg-danger-subtle fs-normal text-danger px-3 py-2 mb-4">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {t("pocketAlternative.hero.badge")}
                </div>
              </div>
              <h1 className="display-4 fw-bold mb-4">
                {t("pocketAlternative.hero.title")}
              </h1>
              <p className="lead mb-4">
                {t("pocketAlternative.hero.description")}
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-primary btn-lg px-5"
                >
                  <i className="bi bi-box-arrow-in-right me-2"></i>
                  {t("pocketAlternative.hero.cta")}
                </Link>
                <Link
                  to="#migration-guide"
                  className="btn btn-outline-primary btn-lg px-4"
                >
                  {t("pocketAlternative.hero.ctaSecondary")}
                </Link>
              </div>
              <p className="mt-3 text-muted">
                <i className="bi bi-shield-check me-2"></i>
                {t("pocketAlternative.hero.guarantee")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-4 bg-white border-bottom">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-12">
              <div className="d-flex justify-content-center align-items-center gap-5 flex-wrap">
                <div className="text-center">
                  <i className="bi bi-lightning-charge h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">Import your Pocket export in minutes</small>
                </div>
                <div className="text-center">
                  <i className="bi bi-shield-lock h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">Zero-knowledge encryption</small>
                </div>
                <div className="text-center">
                  <i className="bi bi-robot h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">AI-powered organization</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why onBookmarks is Better Than Pocket */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-4">
              {t("pocketAlternative.whyBetter.title")}
            </h2>
            <p className="lead text-muted">
              {t("pocketAlternative.whyBetter.description")}
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-success-subtle rounded-3 mb-3">
                    <i className="bi bi-lightning-charge fs-3 text-success"></i>
                  </div>
                  <h3 className="h5 mb-3">
                    {t("pocketAlternative.whyBetter.migration.title")}
                  </h3>
                  <p className="text-muted mb-3">
                    {t("pocketAlternative.whyBetter.migration.description")}
                  </p>
                  <ul className="list-unstyled small text-muted">
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      {t("pocketAlternative.whyBetter.migration.benefit1")}
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      {t("pocketAlternative.whyBetter.migration.benefit2")}
                    </li>
                    <li>
                      <i className="bi bi-check text-success me-2"></i>
                      {t("pocketAlternative.whyBetter.migration.benefit3")}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-light rounded-3 mb-3">
                    <i className="bi bi-robot fs-3 text-primary"></i>
                  </div>
                  <h3 className="h5 mb-3">
                    {t("pocketAlternative.whyBetter.ai.title")}
                  </h3>
                  <p className="text-muted mb-3">
                    {t("pocketAlternative.whyBetter.ai.description")}
                  </p>
                  <ul className="list-unstyled small text-muted">
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      {t("pocketAlternative.whyBetter.ai.benefit1")}
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      {t("pocketAlternative.whyBetter.ai.benefit2")}
                    </li>
                    <li>
                      <i className="bi bi-check text-success me-2"></i>
                      {t("pocketAlternative.whyBetter.ai.benefit3")}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-warning-subtle rounded-3 mb-3">
                    <i className="bi bi-shield-lock fs-3 text-warning"></i>
                  </div>
                  <h3 className="h5 mb-3">
                    {t("pocketAlternative.whyBetter.privacy.title")}
                  </h3>
                  <p className="text-muted mb-3">
                    {t("pocketAlternative.whyBetter.privacy.description")}
                  </p>
                  <ul className="list-unstyled small text-muted">
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      {t("pocketAlternative.whyBetter.privacy.benefit1")}
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      {t("pocketAlternative.whyBetter.privacy.benefit2")}
                    </li>
                    <li>
                      <i className="bi bi-check text-success me-2"></i>
                      {t("pocketAlternative.whyBetter.privacy.benefit3")}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Migration Guide */}
      <section id="migration-guide" className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="text-center mb-5">
                <h2 className="display-6 fw-bold mb-4">
                  {t("pocketAlternative.migrationGuide.title")}
                </h2>
                <p className="lead text-muted">
                  {t("pocketAlternative.migrationGuide.description")}
                </p>
              </div>

              <div className="card shadow-sm border-0">
                <div className="card-body p-5">
                  <div className="row g-4">
                    <div className="col-12">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: "40px",
                            height: "40px",
                            minWidth: "40px",
                          }}
                        >
                          <span className="fw-bold">
                            {t("pocketAlternative.migrationGuide.step1.number")}
                          </span>
                        </div>
                        <div>
                          <h4 className="h5 mb-2">
                            {t("pocketAlternative.migrationGuide.step1.title")}
                          </h4>
                          <p className="text-muted mb-0">
                            {t(
                              "pocketAlternative.migrationGuide.step1.description"
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: "40px",
                            height: "40px",
                            minWidth: "40px",
                          }}
                        >
                          <span className="fw-bold">
                            {t("pocketAlternative.migrationGuide.step2.number")}
                          </span>
                        </div>
                        <div>
                          <h4 className="h5 mb-2">
                            {t("pocketAlternative.migrationGuide.step2.title")}
                          </h4>
                          <p className="text-muted mb-3">
                            {t(
                              "pocketAlternative.migrationGuide.step2.description"
                            )}
                          </p>
                          <Link
                            to={`${process.env.REACT_APP_APP_URL}/register`}
                            className="btn btn-primary"
                          >
                            <i className="bi me-2"></i>
                            {t("pocketAlternative.migrationGuide.step2.button")}
                          </Link>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: "40px",
                            height: "40px",
                            minWidth: "40px",
                          }}
                        >
                          <span className="fw-bold">
                            {t("pocketAlternative.migrationGuide.step3.number")}
                          </span>
                        </div>
                        <div>
                          <h4 className="h5 mb-2">
                            {t("pocketAlternative.migrationGuide.step3.title")}
                          </h4>
                          <p className="text-muted mb-3">
                            {t(
                              "pocketAlternative.migrationGuide.step3.description"
                            )}
                          </p>
                          <div className="p-3 rounded-3 bg-success bg-opacity-10 text-success mb-0">
                            <i className="bi bi-check-circle me-2"></i>
                            <span>
                              {t("pocketAlternative.migrationGuide.step3.info")}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Pocket Users Are Missing - Detailed Benefits */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-4">
              What Pocket Never Gave You
            </h2>
            <p className="lead text-muted">
              Features you didn't know you needed until now
            </p>
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-start">
                    <div className="icon icon-lg bg-purple-subtle rounded-3 me-3 flex-shrink-0">
                      <i className="bi bi-shield-lock fs-3 text-purple"></i>
                    </div>
                    <div>
                      <h3 className="h5 mb-2">True Privacy with Encryption</h3>
                      <p className="text-muted mb-3">
                        Pocket stores all your bookmarks in plain text on their servers. Anyone with access - employees, hackers, or governments - can see everything you've saved.
                      </p>
                      <p className="text-muted mb-3">
                        onBookmarks uses <strong>client-side AES-256 encryption</strong>. Your sensitive bookmarks are encrypted on your device before they ever leave. Not even we can read them.
                      </p>
                      <Link to="/features/encryption" className="btn btn-sm btn-outline-primary">
                        Learn About Our Encryption
                        <i className="bi bi-arrow-right ms-2"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-start">
                    <div className="icon icon-lg bg-primary-subtle rounded-3 me-3 flex-shrink-0">
                      <i className="bi bi-robot fs-3 text-primary"></i>
                    </div>
                    <div>
                      <h3 className="h5 mb-2">AI That Actually Organizes</h3>
                      <p className="text-muted mb-3">
                        Pocket's tagging is 100% manual. Every article you save sits in a flat list unless you manually tag it. Most users give up and end up with chaos.
                      </p>
                      <p className="text-muted mb-3">
                        onBookmarks <strong>automatically suggests tags and collections</strong> based on the content. Save an article about React? It's already tagged and categorized.
                      </p>
                      <Link to="/features/ai-tagging" className="btn btn-sm btn-outline-primary">
                        See AI Tagging in Action
                        <i className="bi bi-arrow-right ms-2"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-start">
                    <div className="icon icon-lg bg-success-subtle rounded-3 me-3 flex-shrink-0">
                      <i className="bi bi-collection fs-3 text-success"></i>
                    </div>
                    <div>
                      <h3 className="h5 mb-2">Multi-Collection Organization</h3>
                      <p className="text-muted mb-3">
                        In Pocket, every article lives in exactly one place. An article about "React performance" can't be in both your "React" folder AND your "Performance" folder without duplicating it.
                      </p>
                      <p className="text-muted mb-3">
                        With onBookmarks, <strong>one bookmark can exist in multiple collections</strong>. No duplicates, no maintenance headaches. Find it wherever it makes sense.
                      </p>
                      <Link to="/features/multi-collection" className="btn btn-sm btn-outline-primary">
                        Explore Multi-Collection
                        <i className="bi bi-arrow-right ms-2"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-start">
                    <div className="icon icon-lg bg-info-subtle rounded-3 me-3 flex-shrink-0">
                      <i className="bi bi-puzzle fs-3 text-info"></i>
                    </div>
                    <div>
                      <h3 className="h5 mb-2">Modern Browser Extensions</h3>
                      <p className="text-muted mb-3">
                        Pocket's browser extension is dated and limited. It saves articles but offers little else - no quick search, no AI suggestions, no side panel.
                      </p>
                      <p className="text-muted mb-3">
                        Our extension includes a <strong>side panel for quick access</strong>, keyboard shortcuts, AI-powered suggestions, and instant sync across all your devices.
                      </p>
                      <Link to="/download/extensions" className="btn btn-sm btn-outline-primary">
                        Get the Extension
                        <i className="bi bi-arrow-right ms-2"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-start">
                    <div className="icon icon-lg bg-warning-subtle rounded-3 me-3 flex-shrink-0">
                      <i className="bi bi-wifi-off fs-3 text-warning"></i>
                    </div>
                    <div>
                      <h3 className="h5 mb-2">Read Offline, Just Like Before</h3>
                      <p className="text-muted mb-3">
                        Pocket's signature feature was saving articles to read offline later. onBookmarks brings that back: your saved articles are cached on your device, so you can read them on a plane or in a dead zone, and a built-in reading list lets you mark what you've read and keep your unread queue clean.
                      </p>
                      <Link to="/features/offline" className="btn btn-sm btn-outline-primary">
                        See Offline Mode
                        <i className="bi bi-arrow-right ms-2"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-start">
                    <div className="icon icon-lg bg-success-subtle rounded-3 me-3 flex-shrink-0">
                      <i className="bi bi-files fs-3 text-success"></i>
                    </div>
                    <div>
                      <h3 className="h5 mb-2">A Clean Import, Not a Cluttered One</h3>
                      <p className="text-muted mb-3">
                        Years of Pocket saves often include the same link two or three times. onBookmarks flags duplicates as you import, so your new library starts clean instead of inheriting the mess. No manual de-duplication afterward.
                      </p>
                      <Link to="/features/duplicate-detection" className="btn btn-sm btn-outline-primary">
                        How Duplicate Detection Works
                        <i className="bi bi-arrow-right ms-2"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Switch Now - Urgency Section */}
      <section className="py-5 bg-danger bg-opacity-10">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8 mx-auto text-center">
              <div className="d-inline-block rounded-4 bg-danger-subtle text-danger px-3 py-2 mb-4">
                <i className="bi bi-info-circle me-2"></i>
                Pocket Has Closed for Good
              </div>
              <h2 className="h2 fw-bold mb-4">
                Give Your Saved Pocket File a New Home
              </h2>
              <p className="lead text-muted mb-4">
                Pocket shut down on July 8, 2025, and Mozilla's export tool went offline in November 2025. If you saved your export before then, onBookmarks is the easiest place to bring those articles back to life.
              </p>
              <div className="row g-4 mb-4">
                <div className="col-md-4">
                  <div className="text-center">
                    <i className="bi bi-file-earmark-check fs-1 text-primary mb-2"></i>
                    <h4 className="h6 mb-1">Already Exported?</h4>
                    <p className="text-muted small mb-0">Import your saved Pocket file in seconds</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-center">
                    <i className="bi bi-arrow-repeat fs-1 text-success mb-2"></i>
                    <h4 className="h6 mb-1">Import in Seconds</h4>
                    <p className="text-muted small mb-0">Our importer handles your entire Pocket library</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-center">
                    <i className="bi bi-shield-check fs-1 text-primary mb-2"></i>
                    <h4 className="h6 mb-1">Future-Proof</h4>
                    <p className="text-muted small mb-0">Your data, your exports, always available</p>
                  </div>
                </div>
              </div>
              <Link
                to={`${process.env.REACT_APP_APP_URL}/register`}
                className="btn btn-danger btn-lg px-5"
              >
                <i className="bi bi-box-arrow-in-right me-2"></i>
                Migrate My Pocket Data Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="text-center mb-5">
                <h2 className="display-6 fw-bold mb-4">
                  {t("pocketAlternative.comparison.title")}
                </h2>
              </div>

              <div className="table-responsive rounded-3 border shadow-sm">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th scope="col" className="p-3">
                        {t("pocketAlternative.comparison.features.feature")}
                      </th>
                      <th scope="col" className="p-3 text-center bg-light">
                        <i className="bi bi-bookmark-x me-2 text-muted"></i>
                        Pocket
                      </th>
                      <th
                        scope="col"
                        className="p-3 text-center d-flex align-items-center justify-content-center gap-2"
                      >
                        <LogoOnly width="20" />
                        onBookmarks
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-3 fw-medium">
                        {t(
                          "pocketAlternative.comparison.features.saveArticles"
                        )}
                      </td>
                      <td className="p-3 text-center bg-light">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                      <td className="p-3 text-center">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">
                        {t("pocketAlternative.comparison.features.tagging")}
                      </td>
                      <td className="p-3 text-center bg-light">
                        {t(
                          "pocketAlternative.comparison.features.taggingValues.pocket"
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <strong>
                          {t(
                            "pocketAlternative.comparison.features.taggingValues.onBookmarks"
                          )}
                        </strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Privacy & Encryption</td>
                      <td className="p-3 text-center bg-light">
                        <i className="bi bi-x-circle text-danger"></i>
                      </td>
                      <td className="p-3 text-center">
                        <strong>Client-Side Encryption</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Multi-Device Sync</td>
                      <td className="p-3 text-center bg-light">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                      <td className="p-3 text-center">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Browser Extensions</td>
                      <td className="p-3 text-center bg-light">
                        Limited browsers
                      </td>
                      <td className="p-3 text-center">
                        <strong>All Major Browsers</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Search Capabilities</td>
                      <td className="p-3 text-center bg-light">
                        Title & Tag Search (no AI)
                      </td>
                      <td className="p-3 text-center">
                        <strong>Advanced + Full-Text</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Folder Organization</td>
                      <td className="p-3 text-center bg-light">
                        Single folder only
                      </td>
                      <td className="p-3 text-center">
                        <strong>Multi-Collection</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Workspace Support</td>
                      <td className="p-3 text-center bg-light">
                        <i className="bi bi-x-circle text-danger"></i>
                      </td>
                      <td className="p-3 text-center">
                        <strong>Multiple Spaces</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Company Status</td>
                      <td className="p-3 text-center text-danger" style={{backgroundColor: "#fff5f5"}}>
                        <strong>Shut down</strong>
                      </td>
                      <td className="p-3 text-center text-success">
                        <strong>Growing & Stable</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h3 className="h2 fw-bold mb-4">
              {t("pocketAlternative.faq.title")}
            </h3>
          </div>

          <div className="row g-4">
            {faqData.map((faq, index) => (
              <div key={index} className="col-lg-4 col-md-6">
                <div className="h-100">
                  <h5 className="fw-bold mb-3">
                    {t(faq.questionKey)}
                  </h5>
                  <p className="text-muted">
                    {t(faq.answerKey)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-5 banner-gradient text-white">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h4 className="h1 fw-bold mb-4 text-white">
                {t("pocketAlternative.finalCta.title")}
              </h4>
              <p className="lead mb-4">
                {t("pocketAlternative.finalCta.description")}
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-light btn-lg px-5"
                >
                  <i className="bi bi-rocket-takeoff me-2"></i>
                  {t("pocketAlternative.finalCta.primaryButton")}
                </Link>
                <Link
                  to="#migration-guide"
                  className="btn btn-outline-light btn-lg px-4"
                >
                  <i className="bi bi-map me-2"></i>
                  {t("pocketAlternative.finalCta.secondaryButton")}
                </Link>
              </div>
              <p className="mt-4 mb-0 opacity-75">
                <i className="bi bi-clock me-2"></i>
                {t("pocketAlternative.finalCta.timeNote")}
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PocketAlternative;
